import * as _ from "lodash"
import {IBusinessObject, IBusinessObjectCreateLoadOptions} from "../PA.Core/PA.Core.IF.BO"
import {IMessageProvider, IProgressProvider, MessageType} from "../PA.Core/PA.Core.IF.Basic"
import {PAdbContext} from "../PA.Core/PAdbContext"
import {IdentityProvider} from "../PA.Core/Basic/Identity"
import {MessageProvider} from "../PA.Core/Basic/Messages"
import {ProgressProvider} from "../PA.Core/Basic/Progress"
import {
    WB_CustomerRow,
    WB_FinishedProductRow,
    WB_ModelPredictorRow,
    WB_ModelProfileRow,
    WB_PeriodRow,
    WB_PlanProductGroupRow,
    WB_ProductGroupRow,
    WB_SimulationInputRelationshipRow,
    WB_SimulationInputRow,
    WB_SimulationMetricsRow,
    WB_SimulationPredictorRelationshipRow,
    WB_SimulationProductRow,
    WB_SimulationRow,
    WB_SpendFunctionRelationshipRow,
    WB_SpendFunctionRelationshipScopeRow,
    WB_SpendFunctionRow
} from "../PA.Core/PAdbSchemaRows"
import {PASearch} from "../PA.Core/PASearch"

import {
    esWB_Customer,
    esWB_Customer_Hierarchy,
    esWB_Finished_Product,
    esWB_MasterData_Set,
    esWB_Model,
    esWB_Model_Coeff_Set,
    esWB_Model_Profile,
    esWB_Model_Profile_Group,
    esWB_Model_Profile_Group_Predictor,
    esWB_Period,
    esWB_Period_Hierarchy,
    esWB_Product_Group,
    esWB_Product_Hierarchy,
    esWB_Published_Model,
    esWB_SalesOrg,
    esWB_Simulation,
    esWB_Simulation_Input,
    esWB_Simulation_Input_Relationship,
    esWB_Simulation_Metrics,
    esWB_Simulation_Metrics_CBO_Set,
    esWB_Simulation_Predictor_Relationship,
    esWB_Simulation_Product,
    esWB_Simulation_Relationship,
    esWB_Spend_Function,
    esWB_Spend_Function_Input_Relationship,
    esWB_Spend_Function_Relationship,
    esWB_Spend_Function_Relationship_Scope,
    esWB_User
} from "../PA.Core/PAdbSchema"
import {ESRecord} from "../PA.Core/lib/ESRecord"
import {ESDataStore} from "../PA.Core/lib/ESDataStore"
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {PAEnvironment} from "../PA.Core/PAEnvironment"
import {IHaveModel} from "../PA.Model.Core/PA.Model.IF.Aspects"
import {
    IHaveSimulationCalculator,
    ISimulationCalculator,
    ISimulationSummaryDetail
} from "../PA.Simulation.Services/SimulationServices.IF"
import {ModelBO} from "../PA.Model.BusinessObjects/ModelBO"
import {SimulationCalculator} from "../PA.Simulation.Services/SimulationCalculator"
import {ISimulation, ISimulationChangedEvent, ISimulationModel} from "../PA.Simulation.Core/PA.Simulation.IF.Aspects"
import {IHaveProductGroups} from "../PA.Core/PA.Core.IF.Aspects"
import {BehaviorSubject} from "rxjs"
import {
    ICellMetric,
    ICubePerspective,
    ICubePerspectiveOptions,
    ICubeSource,
    ICubeSourceChangeEvent,
    IDimensionCompositeKeys,
    IDimensionDistinctKeys
} from "../PA.HyperRectangle.Core/PA.HyperRectangle.IF"
import {DimensionType, IHierarchy} from "../PA.Core/PA.Core.IF.Hierarchy"
import {Perspective} from "../PA.HyperRectangle.Core/Perspective"
import {SimulationPredictorInputCell} from "./SimulationPredictorInputCell"
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer"
import {PeriodService} from "../PA.MD.Services/PeriodService"
import {PeriodHierarchy} from "../PA.MD.Services/PeriodHierarchy"
import {ProductHierarchy} from "../PA.MD.Services/ProductHierarchy"
import {SimulatorPredictorMetric} from "./SimulatorPredictorMetric"
import {SimulationBOValidation} from "./SimulationBO.validation"
import {DimensionPlacement} from "../PA.Reporting.Core/PA.Reporting.IF"
import {CellMetricUtils} from "../PA.HyperRectangle.Core/CellMetricUtils"
import {CoefficientSetUtils, ICoefficientSetSubModel} from "../PA.Model.Core/CoefficientSetUtils"
import {IHaveSimulationFunctions} from "../PA.Simulation.Core/PA.FinancialModel.IF"
import {IwbProductCondition} from "../PA.Core/PAdbSchemaRowsIF"
import {EntityTypeUtils} from "../PA.MD.Entity/EntityTypeUtils"
import {PolicyService} from "../PA.MD.Services/PolicyService"
import {PolicyPricingEngine} from "../PA.MD.Core/PolicyPricingEngine"
import { LeaseRegistry } from "../PA.Utils/LeaseRegistry"

export interface ISimulationBOCreateLoadOptions
    extends IBusinessObjectCreateLoadOptions
{
    wbScnPlanId?: number
}

export class SimulationBO
    implements IBusinessObject,
        ISimulation,
        ICubeSource,
        IHaveModel,
        IHaveSimulationCalculator,
        IHaveProductGroups,
        IHaveSimulationFunctions {
    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: ISimulationBOCreateLoadOptions
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_Simulation: WB_SimulationRow

    arrModel: ISimulationModel[]

    summaryRow: ISimulationSummaryDetail
    summaryDetailRows: ISimulationSummaryDetail[]
    summaryDetailTotalRows: ISimulationSummaryDetail[]

    constructor(options?: any) {
        this.CreateOptions = options || {}

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({id: 0, name: "New Simulation"})

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = null
        this.LoadProgressProvider = new ProgressProvider("Loading", this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider("Validating", this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider("Saving", this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this)

        this.drWB_Simulation = null

        this.arrModel = []
        this.summaryRow = null
        this.summaryDetailRows = []
        this.summaryDetailTotalRows = []

        this.ISimulation_init()
        this.CustomerProducts_init()
        this.IHaveModel_init()
        this.IHaveProductGroups_init()
        this.IHaveSimulationCalculator_init()
        this.IHaveSimulationPredictorsAndInputs_init()
        this.ICubeSource_init()
        this.IHaveSimulationFunctions_Init()
        this.SimulationInputs_init()
    }

    get WB_Simulation(): WB_SimulationRow {
        return this.drWB_Simulation
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: ISimulationBOCreateLoadOptions): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start("Creating Simulation")
        this._OnLoadComplete = (async () => {
            try {
                const opts = (self.LoadOptions = options || {})

                if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
                else self.dbContext = new PAdbContext()

                if (_.isEmpty(self.dbContext)) {
                    const sError = "Unable to determine dbContext for New Simulation."
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.exception(sError)
                    return self
                }

                const usrContext = PAEnvironment.getValue(PAEnvironment.UserContext)
                self.drWB_Simulation = <WB_SimulationRow>await self.dbContext.createRecordWithKey(esWB_Simulation)
                self.WB_Simulation.cif.SIMULATION_NAME = opts.sName || self.WB_Simulation.cif.SIMULATION_NAME
                self.WB_Simulation.cif.WB_MDSET_ID = opts.wbMasterDataSetId
                self.WB_Simulation.cif.WB_SALESORG_ID = opts.wbSalesOrgId
                self.WB_Simulation.cif.WB_PUBLISHED_MODEL_ID = opts.wbPublishedModelId
                self.WB_Simulation.cif.WB_MODEL_PROFILE_ID = opts.wbModelProfileId
                self.WB_Simulation.cif.WB_CUSTH_ID = opts.wbCustHId
                self.WB_Simulation.cif.WB_PRDH_ID = opts.wbPrdHId
                self.WB_Simulation.cif.WB_PERIODH_ID = opts.wbPeriodHId
                self.WB_Simulation.cif.WB_SCNPLAN_ID=opts.wbScnPlanId
                self.dbContext.add(esWB_Simulation, self.drWB_Simulation)

                // Create record using dbContext
                const tasks: Promise<any>[] = []
                if (opts.wbMasterDataSetId > 0)
                    tasks.push(
                        self.dbContext.find(esWB_MasterData_Set, opts.wbMasterDataSetId).then((a) => {
                            self.WB_Simulation.WB_MasterDataSetRow = a
                        })
                    )
                if (opts.wbSalesOrgId > 0)
                    tasks.push(
                        self.dbContext.find(esWB_SalesOrg, opts.wbSalesOrgId).then((a) => {
                            self.WB_Simulation.WB_SalesOrgRow = a
                        })
                    )
                if (opts.wbPublishedModelId > 0)
                    tasks.push(
                        self.dbContext.find(esWB_Published_Model, opts.wbPublishedModelId).then((a) => {
                            self.WB_Simulation.WB_PublishedModelRow = a
                        })
                    )
                if (opts.wbModelProfileId > 0)
                    tasks.push(
                        self.dbContext.find(esWB_Model_Profile, opts.wbModelProfileId).then((a) => {
                            self.WB_Simulation.WB_ModelProfileRow = a
                        })
                    )
                if (opts.wbCustHId > 0) tasks.push(self.dbContext.find(esWB_Customer_Hierarchy, opts.wbCustHId))
                if (opts.wbPrdHId) tasks.push(self.dbContext.find(esWB_Product_Hierarchy, opts.wbPrdHId))
                if (opts.wbPeriodHId > 0) tasks.push(self.dbContext.find(esWB_Period_Hierarchy, opts.wbPeriodHId))

                self.drWB_Simulation.WB_UserRow_CreatedBy = usrContext.User

                await Promise.all(tasks)
                self.Identity.setAll(
                    self.WB_Simulation.cif.WB_SIMULATION_ID,
                    self.WB_Simulation.cif.SIMULATION_ID,
                    self.WB_Simulation.cif.SIMULATION_NAME
                )
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")")
                self.Identity.listenToRecordUpdates(
                    self.WB_Simulation,
                    "WB_SIMULATION_ID",
                    "SIMULATION_ID",
                    "SIMULATION_NAME"
                )
                self._bIsLoaded = true
                await self._AfterBoLoadCompleteInternal()

                // Default Metric Compare List for new Simulation
                const persOpts = self.drWB_Simulation.ComparePerspectiveOptions
                const compTblOpts = self.drWB_Simulation.CompareTableOptions
                compTblOpts.MetricOrder = []
                compTblOpts.MetricPlacement = DimensionPlacement.OnRows
                persOpts.MetricIds = compTblOpts.MetricOrder
                self.drWB_Simulation.ComparePerspectiveOptions = persOpts
                self.drWB_Simulation.CompareTableOptions = compTblOpts

                self.LoadProgressProvider.finish()
                self._OnLoadComplete = undefined
                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err)
                throw err
            }
        })()

        return this._OnLoadComplete
    }

    Load(anId: number, options?: object): Promise<IBusinessObject> {
        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")")

        const rv = (self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
            if (self.bIsLoaded) {
                const sError = "Simulation Type already loaded."
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            self.LoadOptions = options || {}

            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext
            else if (self.dbContext == null) self.dbContext = new PAdbContext()

            if (this.dbContext == null) {
                const sError = "Unable to determine dbContext for Simulation."
                self._OnLoadComplete = null
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = SimulationBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = SimulationBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    const newSimulationRow: any = recordResolver.get(esWB_Simulation, anId)

                    if (_.isEmpty(newSimulationRow)) {
                        self._OnLoadComplete = null
                        self.LoadProgressProvider.exception("Id not found.")
                        resolve(self)
                        return
                    }

                    self.drWB_Simulation = newSimulationRow
                    self.Identity.setAll(
                        self.WB_Simulation.cif.WB_SIMULATION_ID,
                        self.WB_Simulation.cif.SIMULATION_ID,
                        self.WB_Simulation.cif.SIMULATION_NAME
                    )
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    )
                    self.Identity.listenToRecordUpdates(
                        self.WB_Simulation,
                        "WB_SIMULATION_ID",
                        "SIMULATION_ID",
                        "SIMULATION_NAME"
                    )
                    self._bIsLoaded = true
                    return self._AfterBoLoadCompleteInternal()
                })
                .then((newSimulationRow: any) => {
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.finish()
                    resolve(self)
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })
        }))

        return rv
    }

    private async _AfterBoLoadCompleteInternal(): Promise<void> {
        const self = this

        try {
            await self.SimulationInputs_onLoadComplete()
            await self.CustomerProducts_OnLoadComplete()

            const tasks: Promise<any>[] = []
            tasks.push(self.OnISimulationLoaded())
            tasks.push(self.OnIHaveModelLoaded)
            tasks.push(self.IHaveSimulationCalculator_OnLoadComplete())
            tasks.push(self.OnIHaveProductGroupsLoaded)
            tasks.push(self.OnLoadSummary())
            await Promise.all(tasks)

            await self.IHaveSimulationPredictorsAndInputs_afterLoadComplete()

            await self.OnICubeSourceLoadComplete
        } catch (err) {
            console.log(err)
            throw err
        }
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this

        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start(
            "Validating " +
            self.drWB_Simulation.cif.SIMULATION_NAME +
            "(" +
            self.drWB_Simulation.cif.SIMULATION_ID +
            ")"
        )

        // Does no validations yet
        self.ValidateOptions = options || {}

        if (_.isEmpty(self.dbContext)) {
            const sError = "Unable to determine dbContext to Validate Simulation."
            self.ValidateProgressProvider.exception(sError)
            throw sError
        }

        try {
            await SimulationBOValidation.Validation(self)

            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                self._bIsValidated = false
            } else {
                self._bIsValidated = true
            }
            self.ValidateProgressProvider.finish()
        } catch (err) {
            self._bIsValidated = false
            self.ValidateProgressProvider.exception(err)
            return self
        }
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this

        this.SaveProgressProvider.start(
            "Saving " + self.WB_Simulation.cif.SIMULATION_NAME + "(" + self.WB_Simulation.cif.SIMULATION_ID + ")"
        )

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // We could search the tree if there is any record where store isn't set.

        if (_.isEmpty(self.dbContext)) {
            self._bIsSaved = false
            const sError = "Unable to determine dbContext to Save Simulation"
            self.SaveProgressProvider.exception(sError)
            throw sError
        }

        try {
            self.SaveOptions = options || {}

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false
            await self.Validate({})

            if (!self.bIsValidated) {
                self._bIsSaved = false
                bDone = true
                self.SaveProgressProvider.finish()

                return self
            }

            await saveFunction(esWB_Simulation, self.drWB_Simulation, {})
            const tasks: Promise<any>[] = []
            tasks.push(saveFunction(esWB_Simulation_Relationship, self.drWB_Simulation.WB_SimulationRelationshipRows))
            tasks.push(
                saveFunction(
                    esWB_Simulation_Input_Relationship,
                    self.drWB_Simulation.WB_SimulationInputRelationshipRows
                )
            )
            tasks.push(
                saveFunction(
                    esWB_Simulation_Predictor_Relationship,
                    self.drWB_Simulation.WB_SimulationPredictorRelationshipRows
                )
            )
            tasks.push(saveFunction(esWB_Simulation_Product, self.drWB_Simulation.WB_SimulationProductRows))
            tasks.push(
                saveFunction(esWB_Spend_Function_Relationship, self.drWB_Simulation.WB_SpendFunctionRelationshipRows)
            )
            tasks.push(
                saveFunction(
                    esWB_Spend_Function_Relationship_Scope,
                    _.flatMap(
                        self.drWB_Simulation.WB_SpendFunctionRelationshipRows,
                        (a) => a.WB_SpendFunctionRelationshipScopeRows
                    )
                )
            )

            await Promise.all(tasks)

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true
                })
                LeaseRegistry.generateLeaseInvalidationByClass(
                    LeaseRegistry.getLeaseClass(
                        LeaseRegistry.Lease_Subject_Simulation,
                        self.drWB_Simulation.cif.WB_SIMULATION_ID
                    )
                )
            } else {
                self._bIsSaved = true
                LeaseRegistry.generateLeaseInvalidationByClass(
                    LeaseRegistry.getLeaseClass(
                        LeaseRegistry.Lease_Subject_Simulation,
                        self.drWB_Simulation.cif.WB_SIMULATION_ID
                    )
                )
            }
            self.SaveProgressProvider.finish()
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err)
            return self
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start(
            "Deleting " + self.drWB_Simulation.cif.SIMULATION_NAME + "(" + self.drWB_Simulation.cif.SIMULATION_ID + ")"
        )

        return new Promise<IBusinessObject>((resolve, error) => {
            _.forEach(self.drWB_Simulation.WB_SimulationInputRelationshipRows, (aRow) => {
                if (aRow.isActive) {
                    aRow.markDeleted()
                }
            })
            _.forEach(self.drWB_Simulation.WB_SimulationPredictorRelationshipRows, (aRow) => {
                if (aRow.isActive) {
                    aRow.markDeleted()
                }
            })
            _.forEach(self.drWB_Simulation.WB_SimulationProductRows, (aRow) => {
                if (aRow.isActive) {
                    aRow.markDeleted()
                }
            })

            _.forEach(self.drWB_Simulation.WB_SimulationMetricsRows, (aRow) => {
                if (aRow.isActive) {
                    aRow.markDeleted()
                }
            })

            self.drWB_Simulation.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    //</editor-fold>

    Copy(options?: any): Promise<IBusinessObject> {
        const self: SimulationBO = this

        return new Promise<SimulationBO>((resolve, error) => {
            self.ValidateMessageProvider.clearMessages()
            const newSimulationBO = new SimulationBO({dbContext: self.dbContext, bEagerLoadAspectInterfaces: false})
            const deletedPrdGrps: WB_ProductGroupRow[] = []

            const modelPredictors = _.filter(
                self.WB_Simulation.WB_ModelProfileRow?.WB_ModelProfilePredictorRows,
                (aRow) => aRow?.isActive,
            )

            //check if the event is valid to be copied
            newSimulationBO
                .LoadNew({
                    dbContext: self.dbContext,
                    sName: self.WB_Simulation.cif.SIMULATION_NAME + " - Copy",
                    wbMasterDataSetId: self.WB_Simulation.cif.WB_MDSET_ID,
                    wbSalesOrgId: self.WB_Simulation.cif.WB_SALESORG_ID,
                    wbPublishedModelId: self.WB_Simulation.cif.WB_PUBLISHED_MODEL_ID,
                    wbModelProfileId: self.WB_Simulation.cif.WB_MODEL_PROFILE_ID,
                    wbCustHId: self.WB_Simulation.cif.WB_CUSTH_ID,
                    wbPrdHId: self.WB_Simulation.cif.WB_PRDH_ID,
                    wbPeriodHId: self.WB_Simulation.cif.WB_PERIODH_ID
                })
                .then(() => {
                    async function copySequential() {
                        const newRecord = newSimulationBO.WB_Simulation

                        newRecord.cif.WB_CUST_ID = self.WB_Simulation.cif.WB_CUST_ID
                        newRecord.cif.START_DT = self.WB_Simulation.cif.START_DT
                        newRecord.cif.END_DT = self.WB_Simulation.cif.END_DT
                        newRecord.cif.COMPARE_PERSPECTIVE_OPTIONS = self.WB_Simulation.cif.COMPARE_PERSPECTIVE_OPTIONS
                        newRecord.cif.COMPARE_TABLE_OPTIONS = self.WB_Simulation.cif.COMPARE_TABLE_OPTIONS
                        newRecord.cif.METRIC_PRD_COUNT = self.WB_Simulation.cif.METRIC_PRD_COUNT
                        newRecord.cif.WB_AUTHUSR_ID_CREATEDBY = self.WB_Simulation.cif.WB_AUTHUSR_ID_CREATEDBY
                        newRecord.cif.WB_TREE_FOLDER_ID = self.WB_Simulation.cif.WB_TREE_FOLDER_ID
                        newRecord.cif.WB_TREE_FOLDER_ID_SHARED = self.WB_Simulation.cif.WB_TREE_FOLDER_ID_SHARED
                        newRecord.cif.WB_SCNPLAN_ID = self.WB_Simulation.cif.WB_SCNPLAN_ID
                        newRecord.cif.EMBEDDED_IND = self.WB_Simulation.cif.EMBEDDED_IND

                        //Copy Product Groups
                        let prdGrpRows = _.filter(
                            self.WB_Simulation.WB_SimulationProductRows,
                            (aPrdGrpRow: WB_SimulationProductRow) => aPrdGrpRow.isActive
                        )
                        if(!_.isEmpty(options) && ("wbPrdGrpIds" in options) && !_.isEmpty(options.wbPrdGrpIds)){
                            prdGrpRows = _.filter(
                                prdGrpRows,
                                (aPrdGrpRow: WB_SimulationProductRow) => _.includes(options.wbPrdGrpIds, aPrdGrpRow.cif.WB_PRDGRP_ID)
                            )
                        }
                        for (let prdGrpIdx = 0; prdGrpIdx < prdGrpRows.length; prdGrpIdx++) {
                            const prdGrp = prdGrpRows[prdGrpIdx]
                            await newSimulationBO.addProductGroups([prdGrp.cif.WB_PRDGRP_ID])
                        }

                        //Copy Functions
                        const srcFunctions = _.filter(
                            self.WB_Simulation.WB_SpendFunctionRelationshipRows,
                            (aFncn: WB_SpendFunctionRelationshipRow) => aFncn.isActive
                        )
                        for (let a = 0; a < srcFunctions.length; a++) {
                            const spendFuncRelRow = srcFunctions[a]
                            const newSpendFuncRelRow = await newSimulationBO.addSimulationFunction(
                                spendFuncRelRow.cif.WB_SPEND_FUNCTION_ID,
                                {
                                    [spendFuncRelRow.cif.WB_SPEND_FUNCTION_ID]: spendFuncRelRow.WB_SpendFunctionRow,
                                    FunctionType: spendFuncRelRow.WB_SpendFunctionRow.cif.FUNCTION_TYPE
                                }
                            )
                            newSpendFuncRelRow.cif.INPUT_VALUE_1 = spendFuncRelRow.cif.INPUT_VALUE_1
                            newSpendFuncRelRow.cif.INPUT_VALUE_2 = spendFuncRelRow.cif.INPUT_VALUE_2
                            newSpendFuncRelRow.cif.INPUT_VALUE_3 = spendFuncRelRow.cif.INPUT_VALUE_3
                            newSpendFuncRelRow.cif.INPUT_VALUE_4 = spendFuncRelRow.cif.INPUT_VALUE_4
                            newSpendFuncRelRow.cif.SPEND_NAME = spendFuncRelRow.cif.SPEND_NAME
                            newSpendFuncRelRow.cif.SPEND_ID = spendFuncRelRow.cif.SPEND_ID

                            const scopeRows = _.filter(
                                spendFuncRelRow.WB_SpendFunctionRelationshipScopeRows,
                                (aSpendRelRow) => aSpendRelRow.isActive
                            )
                            for (let ct = 0; ct < scopeRows.length; ct++) {
                                const subModel = scopeRows[ct]
                                await newSimulationBO.addSimulationFunctionSubModels(
                                    newSpendFuncRelRow,
                                    subModel.cif.WB_CUST_ID,
                                    subModel.cif.WB_PROD_ID,
                                    subModel.cif.WB_PRDGRP_ID,
                                    {}
                                )
                            }
                        }

                        await newSimulationBO.Sync_PredictorsAndInputs()

                        //Copy Predictor Relationships
                        const predRelRows = _.filter(
                            self.WB_Simulation.WB_SimulationPredictorRelationshipRows,
                            (aPredRelRow: WB_SimulationPredictorRelationshipRow) => aPredRelRow.isActive
                        )
                        for (let y = 0; y < predRelRows.length; y++) {
                            const predRelRow = predRelRows[y]

                            const aNewRow = <WB_SimulationPredictorRelationshipRow>(
                                _.first(
                                    _.filter(
                                        newSimulationBO.WB_Simulation.WB_SimulationPredictorRelationshipRows,
                                        (aRow) =>
                                            aRow.cif.WB_PRDGRP_ID === predRelRow.cif.WB_PRDGRP_ID &&
                                            aRow.cif.WB_PERIOD_ID == predRelRow.cif.WB_PERIOD_ID
                                    )
                                )
                            )

                            for (let predIdx = 0; predIdx < modelPredictors.length; predIdx++) {
                                const aPredRow = modelPredictors[predIdx]
                                const modelPredId = aPredRow?.WB_ModelPredictorRow?.cif?.MODEL_PREDICTOR_ID
                                const predictorValue = predRelRow.cif[modelPredId]
                                // @ts-expect-error "Dynamic Column"
                                aNewRow?.cif[modelPredId] = predictorValue
                            }
                            console.log(aNewRow)
                        }
                    }

                    if (newSimulationBO.bIsLoaded) {
                        return copySequential()
                    } else {
                        console.log("Simulation BO creation failed")
                        error("Simulation BO creation failed")
                    }
                })
                .then((ignore) => {
                    return newSimulationBO.Save(false)
                })
                .then(() => {
                    resolve(newSimulationBO)
                })
                .catch((err) => {
                    console.log("SimulationBO creation failed:")
                    console.log(err)
                    error(err)
                })
                .catch((err) => {
                    console.log("SimulationBO creation failed:")
                    console.log(err)
                    error(err)
                })
        })
    }

    //<editor-fold desc = "Simulation Inputs">

    // Previously, the input set was determined by the Simulation Type, which has been removed.
    private arrInputs: WB_SimulationInputRow[]

    private SimulationInputs_init() {
        this.arrInputs = []
    }

    private async SimulationInputs_onLoadComplete() {
        const self = this

        self.arrInputs = <WB_SimulationInputRow[]>await self.dbContext.findAll(esWB_Simulation_Input, {
            where: {
                DELETED_IND: {neq: "1"},
                SIMULATION_INPUT_TYPE: {inq: ["COGS", "VCM", "NetListPrice", "GrossListPrice", "FundRate"]}
            }
        })
    }

    //</editor-fold>

    //<editor-fold desc = "Results Summary">

    async OnLoadSummary(): Promise<void> {
        const self = this

        const result: ISimulationSummaryDetail[] = await PASearch.find(
            "vWB_Simulation_Result_Metrics",
            {WB_SIMULATION_ID: self.drWB_Simulation.cif.WB_SIMULATION_ID},
            {WB_SIMULATION_ID: {type: "number"}},
            [],
            0,
            []
        )

        if (!_.isEmpty(result)) self.summaryRow = _.first(result)

        self.summaryDetailTotalRows = <ISimulationSummaryDetail[]>(
            await PASearch.find(
                "vWB_Simulation_Result_Detail_Total",
                {WB_SIMULATION_ID: self.drWB_Simulation.cif.WB_SIMULATION_ID},
                {WB_SIMULATION_ID: {type: "number"}},
                [],
                0,
                []
            )
        )

        self.summaryDetailRows = await PASearch.find(
            "vWB_Simulation_Result_Details",
            {WB_SIMULATION_ID: self.drWB_Simulation.cif.WB_SIMULATION_ID},
            {WB_SIMULATION_ID: {type: "number"}},
            [],
            0,
            []
        )
    }

    async ReloadMetrics(): Promise<void> {
        const self = this

        self.dbContext.purgeCache(esWB_Simulation_Metrics, self.WB_Simulation.WB_SimulationMetricsRows)
        const rr = new ESRecordResolver(self.dbContext, {
            standardConditions: {
                [esWB_Simulation_Metrics]: {DELETED_IND: {notIn: ["1"]}}
            }
        })

        const metrics = await rr.queryAllWithRelationships(esWB_Simulation_Metrics, {
            where: {
                WB_SIMULATION_ID: self.WB_Simulation?.cif.WB_SIMULATION_ID,
                SUMMARY_LEVEL: "CustomerProductPeriod",
                DELETED_IND: {notIn: ["1"]}
            }
        })

        _.forEach(metrics, (aMetric: WB_SimulationMetricsRow) => {
            self.WB_Simulation.WB_SimulationMetricsRows.push(aMetric)
        })

        return
    }

    //</editor-fold>

    //<editor-fold desc = "Input Operations -- Why is this part of the BO? vs the UI controller">

    get MetricSummaryRow(): ISimulationSummaryDetail {
        return this.summaryRow
    }

    get MetricDetailRows(): ISimulationSummaryDetail[] {
        return this.summaryDetailRows
    }

    get MetricDetailTotalRows(): ISimulationSummaryDetail[] {
        return this.summaryDetailTotalRows
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveSimulationPredictorsAndInputs Operations">

    private _onIHaveSimulationPredictorsAndInputsChanged: BehaviorSubject<SimulationBO>
    private _qsSimulationPredictorAndInputs_sync: ExecutionSerializer

    private IHaveSimulationPredictorsAndInputs_init() {
        const self = this
        this._onIHaveSimulationPredictorsAndInputsChanged = new BehaviorSubject<SimulationBO>(self)
        this._qsSimulationPredictorAndInputs_sync = new ExecutionSerializer(
            this,
            this._PredictorsAndInputs_sync.bind(self),
            [{}],
            250,
            false
        )
    }

    private async IHaveSimulationPredictorsAndInputs_afterLoadComplete() {
        const self = this

        //  Watch for Date changes/ PG Changes
        self.SimulationPropertyChanged.subscribe((event) => {
            if (_.isEmpty(event)) return

            switch (event?.PropertyChanged) {
                case "START_DT":
                case "END_DT":
                    self.PredictorsAndInputs_sync()
                    break
            }
        })

        await self.OnIHaveProductGroupsLoaded

        self.ProductGroupsChanged.subscribe((obj) => {
            self.PredictorsAndInputs_sync()
        })
    }

    get OnIHaveSimulationPredictorsAndInputsChanged(): BehaviorSubject<SimulationBO> {
        return this._onIHaveSimulationPredictorsAndInputsChanged
    }

    PredictorsAndInputs_sync(): void {
        //  debounce calls ...
        console.log("Synchronizing PredictorsAndInputs: qsRun")
        this._qsSimulationPredictorAndInputs_sync.Run({})
    }

    async Sync_PredictorsAndInputs(): Promise<void> {
        await this._PredictorsAndInputs_sync({})
    }

    private async _PredictorsAndInputs_sync(opts: any): Promise<void> {
        const self = this

        // Determine the Periods
        const wbEntityId_Period = self.WB_Simulation.WB_PublishedModelRow.cif.WB_ENTITY_ID_PERIOD
        const entityPeriod = await EntityTypeUtils.getEntityTypeRow(wbEntityId_Period)

        const arrPeriods = await PeriodService.getPeriodsByPeriodTypeWbIDDateRange(
            entityPeriod?.cif?.META_WB_ID || 0,
            self.WB_Simulation.StartDate,
            self.WB_Simulation.EndDate
        )

        const arrPrdGrps = self.ProductGroups

        const dctPeriods = _.keyBy(arrPeriods, (a) => a.cif.WB_PERIOD_ID)
        const dctPrdGrps = _.keyBy(arrPrdGrps, (a) => a.cif.WB_PRDGRP_ID)

        const dctPredictors: { [wbPrdGrpId: number]: { [wbPeriodId: number]: WB_SimulationPredictorRelationshipRow } } =
            {}

        // Sync up Predictors
        _.forEach(self.WB_Simulation.WB_SimulationPredictorRelationshipRows, (aRow) => {
            if (!aRow.isActive) return

            // Check if the Row is in Scope
            if (!(aRow.cif.WB_PERIOD_ID in dctPeriods)) {
                aRow.markDeleted()
                return
            }

            if (!(aRow.cif.WB_PRDGRP_ID in dctPrdGrps)) {
                aRow.markDeleted()
                return
            }

            if (!(aRow.cif.WB_PRDGRP_ID in dctPredictors)) {
                dctPredictors[aRow.cif.WB_PRDGRP_ID] = {}
            }

            if (!(aRow.cif.WB_PERIOD_ID in dctPredictors[aRow.cif.WB_PRDGRP_ID])) {
                dctPredictors[aRow.cif.WB_PRDGRP_ID][aRow.cif.WB_PERIOD_ID] = aRow
            } else {
                // duplicate
                aRow.markDeleted()
                return
            }
        })

        // Determine Missing Predictors, Add them
        for (const aPrdGrp of arrPrdGrps) {
            for (const aPeriod of arrPeriods) {
                //--
                const wbPrdGrpId = aPrdGrp.cif.WB_PRDGRP_ID
                const wbPeriodId = aPeriod.cif.WB_PERIOD_ID

                if (!(wbPrdGrpId in dctPredictors)) {
                    dctPredictors[wbPrdGrpId] = {}
                }

                if (!(wbPeriodId in dctPredictors[wbPrdGrpId])) {
                    //-- Add missing
                    const aNewRow = <WB_SimulationPredictorRelationshipRow>(
                        await self.dbContext.createRecordWithKey(esWB_Simulation_Predictor_Relationship)
                    )
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SIMULATION_ID = self.WB_Simulation.cif.WB_SIMULATION_ID
                    aNewRow.WB_SimulationRow = self.WB_Simulation
                    aNewRow.cif.WB_PRDGRP_ID = wbPrdGrpId
                    aNewRow.cif.WB_PERIOD_ID = wbPeriodId
                    self.dbContext.add(esWB_Simulation_Predictor_Relationship, aNewRow)

                    if (!aNewRow.WB_ProductGroupRow)
                        aNewRow.WB_ProductGroupRow = <WB_ProductGroupRow>(
                            (await self.dbContext.findUsingCache(esWB_Product_Group, [wbPrdGrpId]))[0]
                        )

                    if (!aNewRow.WB_PeriodRow)
                        aNewRow.WB_PeriodRow = <WB_PeriodRow>(
                            (await self.dbContext.findUsingCache(esWB_Period, [wbPeriodId]))[0]
                        )

                    dctPredictors[wbPrdGrpId][wbPeriodId] = aNewRow
                }
            } // Next Period
        } // Next Product Group

        const dctInputs: { [wbPrdGrpId: number]: { [wbPeriodId: number]: WB_SimulationInputRelationshipRow } } = {}

        // Sync up Inputs
        _.forEach(self.WB_Simulation.WB_SimulationInputRelationshipRows, (aRow) => {
            if (!aRow.isActive) return

            // Check if the Row is in Scope
            if (!(aRow.cif.WB_PERIOD_ID in dctPeriods)) {
                aRow.markDeleted()
                return
            }

            if (!(aRow.cif.WB_PRDGRP_ID in dctPrdGrps)) {
                aRow.markDeleted()
                return
            }

            if (!(aRow.cif.WB_PRDGRP_ID in dctInputs)) {
                dctInputs[aRow.cif.WB_PRDGRP_ID] = {}
            }

            if (!(aRow.cif.WB_PERIOD_ID in dctInputs[aRow.cif.WB_PRDGRP_ID])) {
                dctInputs[aRow.cif.WB_PRDGRP_ID][aRow.cif.WB_PERIOD_ID] = aRow
            } else {
                // duplicate
                aRow.markDeleted()
                return
            }
        })

        // Determine Missing Inputs, Add them
        for (const aPrdGrp of arrPrdGrps) {
            for (const aPeriod of arrPeriods) {
                //--
                const wbPrdGrpId = aPrdGrp.cif.WB_PRDGRP_ID
                const wbPeriodId = aPeriod.cif.WB_PERIOD_ID

                if (!(wbPrdGrpId in dctInputs)) {
                    dctInputs[wbPrdGrpId] = {}
                }

                if (!(wbPeriodId in dctInputs[wbPrdGrpId])) {
                    //-- Add missing
                    const aNewRow = <WB_SimulationInputRelationshipRow>(
                        await self.dbContext.createRecordWithKey(esWB_Simulation_Input_Relationship)
                    )
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_SIMULATION_ID = self.WB_Simulation.cif.WB_SIMULATION_ID
                    aNewRow.WB_SimulationRow = self.WB_Simulation
                    aNewRow.cif.WB_PRDGRP_ID = wbPrdGrpId
                    aNewRow.cif.WB_PERIOD_ID = wbPeriodId
                    self.dbContext.add(esWB_Simulation_Input_Relationship, aNewRow)

                    if (!aNewRow.WB_ProductGroupRow)
                        aNewRow.WB_ProductGroupRow = <WB_ProductGroupRow>(
                            (await self.dbContext.findUsingCache(esWB_Product_Group, [wbPrdGrpId]))[0]
                        )

                    if (!aNewRow.WB_PeriodRow)
                        aNewRow.WB_PeriodRow = <WB_PeriodRow>(
                            (await self.dbContext.findUsingCache(esWB_Period, [wbPeriodId]))[0]
                        )

                    dctInputs[wbPrdGrpId][wbPeriodId] = aNewRow
                }
            } // Next Period
        } // Next Product Group

        self.OnIHaveSimulationPredictorsAndInputsChanged.next(self)
    }

    //</editor-fold>

    //<editor-fold desc = "ISimulator Operations">

    private _simulationPropertyChanged: BehaviorSubject<ISimulationChangedEvent>

    ISimulation_init() {
        this._simulationPropertyChanged = new BehaviorSubject<ISimulationChangedEvent>(undefined)
    }

    get SimulationPropertyChanged(): BehaviorSubject<ISimulationChangedEvent> {
        return this._simulationPropertyChanged
    }

    OnISimulationLoaded(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.WB_Simulation.setupOnChangeEvents([], self.ISimulation_onRecordChange.bind(self), self)
            resolve()
        })
    }

    private ISimulation_onRecordChange(
        record: WB_SimulationRow,
        propertyName: string,
        propertyValue: any,
        options: SimulationBO
    ) {
        this.SimulationPropertyChanged.next({
            SimulationBO: options,
            Row: record,
            PropertyChanged: propertyName,
            PropertyValue: propertyValue
        })
    }

    //</editor-fold>

    //<editor-fold desc = "ICubeSource Operations">

    private _bICubeSourceLoaded: boolean
    private _onSourcePropertyChange: BehaviorSubject<ICubeSourceChangeEvent>
    private _onICubeSourceLoadComplete: Promise<void>
    private _arrLeafCells: SimulationPredictorInputCell[]

    private _dctDimensionHierarchies: { [DimType: string]: IHierarchy }
    private _dctCellMetrics: { [metricId: string]: ICellMetric }
    private _cubeSourceLoadMessageProvider: MessageProvider
    private _cubeSourceLoadProgressProvider: ProgressProvider
    private _qsCubeSource_syncLeafCells: ExecutionSerializer
    private _dctRefreshListByMetric: { [metricId: string]: string[] }

    private ICubeSource_init() {
        this._onSourcePropertyChange = new BehaviorSubject<{
            Source: ICubeSource;
            PropertyName: string;
            PropertyValue: any;
        }>(undefined)
        this._bICubeSourceLoaded = false
        this._onICubeSourceLoadComplete = undefined
        this._arrLeafCells = []
        this._cubeSourceLoadMessageProvider = new MessageProvider()
        this._cubeSourceLoadProgressProvider = new ProgressProvider()
        this._qsCubeSource_syncLeafCells = new ExecutionSerializer(
            this,
            this._ICubeSource_syncLeafCells.bind(this),
            [{}],
            250,
            false
        )

        this._dctDimensionHierarchies = {}
        this._dctCellMetrics = {}
        this._dctRefreshListByMetric = {}
    }

    get CubeSourceName(): string {
        return this.WB_Simulation?.cif?.SIMULATION_ID + "_" + this.WB_Simulation?.cif?.SIMULATION_NAME
    }

    get CubeSourceLoadMessageProvider(): IMessageProvider {
        return this._cubeSourceLoadMessageProvider
    }

    get CubeSourceLoadProgressProvider(): IProgressProvider {
        return this._cubeSourceLoadProgressProvider
    }

    get OnICubeSourceLoadComplete(): Promise<void> {
        const self = this

        if (self._bICubeSourceLoaded) return Promise.resolve()

        if (!_.isUndefined(self._onICubeSourceLoadComplete)) return self._onICubeSourceLoadComplete

        const returnValue = (self._onICubeSourceLoadComplete = (async () => {
            // Create Dimensions
            await self.OnIHaveModelLoaded
            const modelRow = self.Model.WB_Model

            if (self.WB_Simulation.cif.WB_PRDH_ID && self.WB_Simulation.cif.WB_PERIODH_ID) {
                const aProductHierarchy = new ProductHierarchy(
                    self.WB_Simulation.cif.WB_PRDH_ID,
                    self.WB_Simulation.StartDate,
                    self.WB_Simulation.EndDate
                )
                const aTimeHierarchy = new PeriodHierarchy(self.WB_Simulation.cif.WB_PERIODH_ID)
                await Promise.all([aProductHierarchy.OnLoadComplete, aTimeHierarchy.OnLoadComplete])
                self._dctDimensionHierarchies[DimensionType.Product] = aProductHierarchy
                self._dctDimensionHierarchies[DimensionType.Time] = aTimeHierarchy
            }
            // Create Metrics
            const arrPredictors: WB_ModelPredictorRow[] = []
            _.filter(
                _.orderBy(self.WB_Simulation.WB_ModelProfileRow?.WB_ModelProfileGroupRows, "GROUP_SORT"),
                (group) => {
                    if (!group.isActive) return

                    _.forEach(_.orderBy(group.WB_ModelProfileGroupPredictorRows, "SORT_ORDER"), (predictor) => {
                        if (!predictor.isActive) return
                        predictor.WB_ModelPredictorRow.cif.MODEL_PREDICTOR_NAME = predictor.cif.PREDICTOR_LABEL
                        arrPredictors.push(predictor.WB_ModelPredictorRow)
                    })
                }
            )

            _.forEach(arrPredictors, (aPredictor) => {
                const aPredictorMetric = new SimulatorPredictorMetric(aPredictor)
                self._dctCellMetrics[aPredictorMetric.MetricId] = aPredictorMetric
            })

            /*
                let ids: any = []
                let anInputMetric = new SimulatorInputMetric(aSimInputRow)
                self._dctCellMetrics[anInputMetric.MetricId] = anInputMetric
            */

            self._dctRefreshListByMetric = CellMetricUtils.getRefreshMetricParentsByMetricId(self)

            // Sync Cube Cells
            await self._ICubeSource_syncLeafCells({})

            // Setup Listener on Predictor and Input change
            self.OnIHaveSimulationPredictorsAndInputsChanged.subscribe((obj) => {
                // Sync Leaf Cells
                self.ICubeSource_syncLeafCells()
            })

            self._bICubeSourceLoaded = true
        })())

        return returnValue
    }

    private get CubeSourceLeafCells(): SimulationPredictorInputCell[] {
        return this._arrLeafCells
    }

    get OnCubeSourcePropertyChange(): BehaviorSubject<ICubeSourceChangeEvent> {
        return this._onSourcePropertyChange
    }

    get bICubeSourceLoaded(): boolean {
        return this._bICubeSourceLoaded
    }

    createPerspective(name: string, options: ICubePerspectiveOptions): ICubePerspective {
        return new Perspective(name, this, options, {cells: this.CubeSourceLeafCells})
    }

    getDimensionHierarchy(dimensionType: DimensionType): IHierarchy {
        return this._dctDimensionHierarchies[dimensionType]
    }

    getDimensions(): DimensionType[] {
        return [DimensionType.Product, DimensionType.Time]
    }

    getCompositeLeafKeys(dimensionTypeIds: DimensionType[]): Promise<IDimensionCompositeKeys> {
        //-- TODO: We don't need it until we do filtering of the dimensions
        return Promise.resolve(undefined)
    }

    getDistinctLeafKeys(dimensionTypeIds: DimensionType[]): Promise<IDimensionDistinctKeys> {
        //-- TODO: We don't need it until we do filtering of the dimensions
        return Promise.resolve(undefined)
    }

    getMetric(metricId: string): ICellMetric {
        return this._dctCellMetrics[metricId]
    }

    getMetrics(): ICellMetric[] {
        return _.values(this._dctCellMetrics)
    }

    getRefreshMetricIds(aMetricId: string): string[] {
        return this._dctRefreshListByMetric[aMetricId] || [aMetricId]
    }

    ICubeSource_syncLeafCells(): void {
        //  debounce calls ...
        console.log("Synchronizing Cube Source Leaf Cells: qsRun")
        this._qsCubeSource_syncLeafCells.Run({})
    }

    private async _ICubeSource_syncLeafCells(opts: any): Promise<void> {
        const self = this

        const dctLeafCells: {
            [wbPrdGrpId: string]: {
                [wbPeriodId: string]: {
                    Cell: SimulationPredictorInputCell;
                    Predictor: WB_SimulationPredictorRelationshipRow;
                    Input: WB_SimulationInputRelationshipRow;
                };
            };
        } = {}

        // Read in all the Inputs and Predictors and Place in Dictionary
        _.forEach(self.WB_Simulation.WB_SimulationPredictorRelationshipRows, (aRow) => {
            if (!aRow.isActive) return

            const wbPrdGrpId = aRow.cif.WB_PRDGRP_ID
            const wbPeriodId = aRow.cif.WB_PERIOD_ID

            if (!(wbPrdGrpId in dctLeafCells)) dctLeafCells[wbPrdGrpId] = {}

            if (!(wbPeriodId in dctLeafCells[wbPrdGrpId])) {
                dctLeafCells[wbPrdGrpId][wbPeriodId] = {
                    Cell: undefined,
                    Input: undefined,
                    Predictor: undefined
                }
            }

            if (_.isUndefined(dctLeafCells[wbPrdGrpId][wbPeriodId].Predictor))
                dctLeafCells[wbPrdGrpId][wbPeriodId].Predictor = aRow
            else {
                //-- Error condition, duplicate Predictor detected
            }
        })

        _.forEach(self.WB_Simulation.WB_SimulationInputRelationshipRows, (aRow) => {
            if (!aRow.isActive) return

            const wbPrdGrpId = aRow.cif.WB_PRDGRP_ID
            const wbPeriodId = aRow.cif.WB_PERIOD_ID

            if (!(wbPrdGrpId in dctLeafCells)) dctLeafCells[wbPrdGrpId] = {}

            if (!(wbPeriodId in dctLeafCells[wbPrdGrpId])) {
                dctLeafCells[wbPrdGrpId][wbPeriodId] = {
                    Cell: undefined,
                    Input: undefined,
                    Predictor: undefined
                }
            }

            if (_.isUndefined(dctLeafCells[wbPrdGrpId][wbPeriodId].Input))
                dctLeafCells[wbPrdGrpId][wbPeriodId].Input = aRow
            else {
                //-- Error condition, duplicate Input detected
            }
        })

        //--  Slot in existing Cells
        _.forEach(self.CubeSourceLeafCells, (aCell) => {
            const wbPrdGrpId = aCell.getKey(DimensionType.Product)
            const wbPeriodId = aCell.getKey(DimensionType.Time)

            if (!(wbPrdGrpId in dctLeafCells)) {
                //--PG removed
                return
            }

            if (!(wbPeriodId in dctLeafCells[wbPrdGrpId])) {
                //-- Period removed
                return
            }

            if (_.isUndefined(dctLeafCells[wbPrdGrpId][wbPeriodId].Cell))
                dctLeafCells[wbPrdGrpId][wbPeriodId].Cell = aCell
            else {
                //-- Error condition, duplicate Cell detected
            }
        })

        // Reconstruct the Cell
        const arrNewLeafCells: SimulationPredictorInputCell[] = []
        _.forEach(dctLeafCells, (dctSub, wbPrdGrpId) => {
            _.forEach(dctSub, (group, wbPeriodId) => {
                if (_.isEmpty(group.Input) || _.isEmpty(group.Predictor)) {
                    // Missing one of pair
                    return
                }

                if (_.isEmpty(group.Cell)) {
                    group.Cell = new SimulationPredictorInputCell(self, group.Predictor, group.Input)
                } else if (!group.Cell.rowsMatch(group.Predictor, group.Input)) {
                    group.Cell = new SimulationPredictorInputCell(self, group.Predictor, group.Input)
                }

                arrNewLeafCells.push(group.Cell)
            })
        })

        //
        self._arrLeafCells = arrNewLeafCells
        self.OnCubeSourcePropertyChange.next({Source: self, PropertyName: "Perspective", PropertyValue: undefined})
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveProductGroup operations">

    _bIHaveProductGroupsLoaded: boolean
    _OnIHaveProductGroupsLoaded: Promise<IHaveProductGroups>
    _ProductGroupsAll: WB_ProductGroupRow[]

    IHaveProductGroups_init() {
        this.ProductGroupsChanged = new BehaviorSubject<IHaveProductGroups>(this)
        this._bIHaveProductGroupsLoaded = false
        this._OnIHaveProductGroupsLoaded = undefined
        this._ProductGroupsAll = []
    }

    ProductGroupsChanged: BehaviorSubject<IHaveProductGroups>

    get bIHaveProductGroupsLoaded(): boolean {
        return this._bIHaveProductGroupsLoaded
    }

    get OnIHaveProductGroupsLoaded(): Promise<IHaveProductGroups> {
        const self = this

        if (self.bIHaveProductGroupsLoaded) {
            return ASync.resolve(self)
        }

        if (!_.isEmpty(self._OnIHaveProductGroupsLoaded)) return self._OnIHaveProductGroupsLoaded // Load already started, wait on same Promise

        const rv = (self._OnIHaveProductGroupsLoaded = (async () => {
            try {
                // If the BO is not loaded, throw an error.. It should have been started
                if (!self.bIsLoaded && _.isEmpty(self.OnLoadComplete)) {
                    throw "Simulation isn't Loaded or Loading.. Can't determine Product Groups"
                }

                let boLoadPromise = undefined
                if (self.bIsLoaded) {
                    boLoadPromise = ASync.resolve(self)
                } else {
                    boLoadPromise = self.OnLoadComplete
                }
                await boLoadPromise

                this._bIHaveProductGroupsLoaded = true
                this._OnIHaveProductGroupsLoaded = undefined
                return self
            } catch (err) {
                console.log(err)
                throw err
            }
        })())

        return rv
    }

    get ProductGroups(): WB_ProductGroupRow[] {
        return _.map(
            _.filter(this.WB_Simulation.WB_SimulationProductRows, (aRow) => aRow.isActive),
            (aRow) => aRow.WB_ProductGroupRow
        )
    }

    get ProductGroupsReadOnly(): boolean {
        // TODO: Check Security Roles..
        return false
    }

    addProductGroups(arrProductGroupId: number[]): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self.dbContext
                .findUsingCache(esWB_Product_Group, arrProductGroupId)
                .then((arrPrdGrpRows: WB_ProductGroupRow[]) => {
                    async function createProductGroupRows() {
                        for (const idx in arrPrdGrpRows) {
                            const aWB_PrdGrpRow = arrPrdGrpRows[idx]
                            const newRecord: WB_PlanProductGroupRow = <any>(
                                await self.dbContext.createRecordWithKey(esWB_Simulation_Product)
                            )
                            newRecord.setDefaults()
                            newRecord.cif.WB_SIMULATION_ID = self.WB_Simulation.cif.WB_SIMULATION_ID
                            newRecord.cif.WB_PRDGRP_ID = aWB_PrdGrpRow.cif.WB_PRDGRP_ID
                            newRecord.WB_ProductGroupRow = aWB_PrdGrpRow
                            self.dbContext.add(esWB_Simulation_Product, newRecord)
                        }
                        self.ProductGroupsChanged.next(self)
                    }

                    return createProductGroupRows()
                })
                .then((ignore) => {
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    addProductGroup(ProductGroupId: number): Promise<void> {
        const self = this

        return self.addProductGroups([ProductGroupId])
    }

    removeProductGroups(arrProductGroupId: number[]): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            let bChanged = false
            _.forEach(self.WB_Simulation.WB_SimulationProductRows, (aRow) => {
                if (aRow.isActive && _.includes(arrProductGroupId, aRow.cif.WB_PRDGRP_ID)) {
                    bChanged = true
                    aRow.markDeleted()
                }
            })

            if (bChanged) {
                self.ProductGroupsChanged.next(self)
            }

            resolve()
        })
    }

    removeProductGroup(ProductGroupId: number): Promise<void> {
        const self = this

        return self.removeProductGroups([ProductGroupId])
    }

    hasProductGroup(ProductGroupId: number): boolean {
        const self = this
        let returnValue = false

        _.forEach(self.WB_Simulation.WB_SimulationProductRows, (aRow) => {
            if (aRow.isActive && aRow.cif.WB_PRDGRP_ID == ProductGroupId) {
                returnValue = true
                return false // stop iterating
            }
        })

        return returnValue
    }

    isProductGroupEditable(ProductGroupId: number): boolean {
        return true
    }

    //</editor-fold>

    //<editor-fold desc="IHaveModel Operations">

    private _Model: ModelBO
    private _OnIHaveModelLoaded: Promise<void>
    private _bIHaveModelLoaded: boolean

    IHaveModel_init() {
        this._bIHaveModelLoaded = false
        this._OnIHaveModelLoaded = undefined
        this._Model = undefined
    }

    get bIHaveModelLoaded(): boolean {
        return this._bIHaveModelLoaded
    }

    get OnIHaveModelLoaded(): Promise<void> {
        const self = this

        if (self._OnIHaveModelLoaded) return self._OnIHaveModelLoaded

        const returnValue = (self._OnIHaveModelLoaded = new Promise<void>((resolve, error) => {
            const wbModelId = self.WB_Simulation.WB_PublishedModelRow.cif.WB_MODEL_ID
            if (!(wbModelId > 0)) {
                error(`No Model Specified: ${wbModelId}`)
                return
            }
            const aModel = new ModelBO()
            aModel
                .Load(wbModelId, {dbContext: self.dbContext})
                .then((ignore) => {
                    self._Model = aModel
                    self._bIHaveModelLoaded = true
                    resolve()
                })
                .catch((err) => {
                    error(err)
                })
        }))

        return returnValue
    }

    get Model(): ModelBO {
        return this._Model
    }

    //</editor-fold>

    //<editor-fold desc="IHaveSimulationCalculator operations">

    private _simulationCalculator: ISimulationCalculator

    private IHaveSimulationCalculator_init() {
        this._simulationCalculator = new SimulationCalculator(this)
    }

    private IHaveSimulationCalculator_OnLoadComplete(): Promise<void> {
        const self = this

        return new Promise<void>((resolve, error) => {
            self._simulationCalculator.Register(self)
            resolve()
        })
    }

    private IHaveSimulationCalculator_BeforeReload(): void {
        // Do nothing
    }

    get SimulationCalculator(): ISimulationCalculator {
        return this._simulationCalculator
    }

    //</editor-fold>

    //<editor-fold desc = "Update Prices" >
    async updatePricing(): Promise<void> {
        const self = this

        const wbPolicyIds: number[] = await PolicyService.getPolicyByEnterpriseScope(
            self.WB_Simulation?.cif?.WB_SALESORG_ID || 0,
            self.WB_Simulation?.cif?.START_DT,
            self.WB_Simulation?.cif?.END_DT
        )

        if (_.isEmpty(wbPolicyIds)) {
            throw new Error(
                `Policy is not configured for sales org ${self.WB_Simulation?.WB_SalesOrgRow?.cif.SALESORG_ID} in selected start date and duration`
            )
        }
        const publishedModel = self.WB_Simulation.WB_PublishedModelRow.cif
        let siGrossListPrice: WB_SimulationInputRow = undefined
        let siNetListPrice: WB_SimulationInputRow = undefined
        let siVCM: WB_SimulationInputRow = undefined
        let siCOGS: WB_SimulationInputRow = undefined
        const arrFundingRates: WB_SimulationInputRow[] = []

        _.forEach(self.arrInputs, (aRow) => {
            switch (aRow.cif.SIMULATION_INPUT_TYPE) {
                case "NetListPrice":
                    siNetListPrice = aRow
                    break
                case "GrossListPrice":
                    siGrossListPrice = aRow
                    break
                case "VCM":
                    siVCM = aRow
                    break
                case "COGS":
                    siCOGS = aRow
                    break
                case "FundRate":
                    arrFundingRates.push(aRow)
                    break
            }
        })

        const startDt = self.WB_Simulation.StartDate
        const endDt = self.WB_Simulation.EndDate

        for (const aSimInputRow of self.WB_Simulation.WB_SimulationInputRelationshipRows) {
            if (!aSimInputRow.isActive) continue

            const wbCustId = self.WB_Simulation.cif.WB_CUST_ID

            let wbPrdId = 0
            if (self._bHasProducts) wbPrdId = aSimInputRow.cif.WB_PROD_ID
            if (self._bHasProductGroups) wbPrdId = aSimInputRow.cif.WB_PRDGRP_ID

            let aNetListPriceCondition: IwbProductCondition = undefined
            if (siNetListPrice) {
                aNetListPriceCondition = await PolicyPricingEngine.getCustomerNetListPrice(
                    wbPolicyIds,
                    publishedModel.WB_ENTITY_ID_PRODUCT,
                    publishedModel.WB_ENTITY_ID_CUSTOMER,
                    wbPrdId,
                    self.WB_Simulation.cif.WB_PRDH_ID,
                    wbCustId,
                    0,
                    startDt,
                    endDt
                )

                // @ts-expect-error "Dynamic Column"
                aSimInputRow[siNetListPrice.cif.SIMULATION_INPUT_ID] = aNetListPriceCondition?.VALUE_RATEAMOUNT || 0
            }

            let aGrossListPriceCondition: IwbProductCondition = undefined
            if (siGrossListPrice) {
                aGrossListPriceCondition = await PolicyPricingEngine.getCustomerListPrice(
                    wbPolicyIds,
                    publishedModel.WB_ENTITY_ID_PRODUCT,
                    publishedModel.WB_ENTITY_ID_CUSTOMER,
                    wbPrdId,
                    self.WB_Simulation.cif.WB_PRDH_ID,
                    wbCustId,
                    0,
                    startDt,
                    endDt
                )

                // @ts-expect-error "Dynamic Column"
                aSimInputRow[siGrossListPrice.cif.SIMULATION_INPUT_ID] =
                    aGrossListPriceCondition?.VALUE_RATEAMOUNT || 0
            }

            let aVMCCondition: IwbProductCondition = undefined
            if (siVCM) {
                aVMCCondition = await PolicyPricingEngine.getCustomerVCM(
                    wbPolicyIds,
                    publishedModel.WB_ENTITY_ID_PRODUCT,
                    publishedModel.WB_ENTITY_ID_CUSTOMER,
                    wbPrdId,
                    self.WB_Simulation.cif.WB_PRDH_ID,
                    wbCustId,
                    0,
                    startDt,
                    endDt
                )

                // @ts-expect-error "Dynamic Column"
                aSimInputRow[siVCM.cif.SIMULATION_INPUT_ID] = aVMCCondition?.VALUE_RATEAMOUNT || 0
            }

            let aCOGSCondition: IwbProductCondition = undefined
            if (siCOGS) {
                aCOGSCondition = await PolicyPricingEngine.getCustomerCOGS(
                    wbPolicyIds,
                    publishedModel.WB_ENTITY_ID_PRODUCT,
                    publishedModel.WB_ENTITY_ID_CUSTOMER,
                    wbPrdId,
                    wbCustId,
                    0,
                    startDt,
                    endDt
                )

                // @ts-expect-error "Dynamic Column"
                aSimInputRow[siCOGS.cif.SIMULATION_INPUT_ID] = aCOGSCondition?.VALUE_RATEAMOUNT || 0
            }

            _.forEach(arrFundingRates, async (aRow) => {
                const aRate = await PolicyPricingEngine.getPrdGrpFundingRateTotal(
                    wbPrdId,
                    wbCustId,
                    aRow.cif.WB_FUNDTYPE_ID,
                    0,
                    0,
                    aRow.cif.PRD_CNDM_ID_STD,
                    aRow.cif.PRD_CNDM_ID_ADDL,
                    startDt,
                    endDt
                )

                // @ts-expect-error "Dynamic Column"
                aSimInputRow[aRow.cif.SIMULATION_INPUT_ID] = aRate?.cif?.VALUE_RATEAMOUNT || 0
            })
        }
    }

    //</editor-fold>

    //<editor-fold desc = "Published Model Products">

    private _bHasProductGroups: boolean
    private _bHasProducts: boolean
    private _allCustomers: WB_CustomerRow[]
    private _allProductGroups: WB_ProductGroupRow[]
    private _allProducts: WB_FinishedProductRow[]
    private _subModels: {
        Customer: WB_CustomerRow;
        ProductGroup: WB_ProductGroupRow;
        Product: WB_FinishedProductRow;
    }[]
    private _allSubModels: {
        Customer: WB_CustomerRow;
        ProductGroup: WB_ProductGroupRow;
        Product: WB_FinishedProductRow;
    }[]
    private _onCustomersChanged: BehaviorSubject<SimulationBO>
    private _onProductsChanged: BehaviorSubject<SimulationBO>

    private CustomerProducts_init() {
        this._bHasProductGroups = false
        this._bHasProducts = false
        this._allCustomers = []
        this._allProductGroups = []
        this._allProducts = []
        this._subModels = []
        this._allSubModels = []
        this._onCustomersChanged = new BehaviorSubject<SimulationBO>(undefined)
        this._onProductsChanged = new BehaviorSubject<SimulationBO>(undefined)
    }

    private async CustomerProducts_OnLoadComplete(): Promise<void> {
        const self = this

        //
        if (_.isEmpty(this.WB_Simulation?.WB_PublishedModelRow)) return

        const prdEntity = await EntityTypeUtils.getEntityTypeRow(
            this.WB_Simulation.WB_PublishedModelRow.cif.WB_ENTITY_ID_PRODUCT
        )

        if (_.isEmpty(prdEntity)) return

        this._bHasProducts = prdEntity.cif.ENTITY_ID in {FinishedProduct: 0, ConsumerProduct: 1}
        this._bHasProductGroups = !this._bHasProducts

        const wbCoefSetId = this.WB_Simulation?.WB_PublishedModelRow?.cif?.WB_MODEL_COEFF_SET_ID

        const a = await CoefficientSetUtils.getCoefficientSetMasterData(wbCoefSetId, this.dbContext, true)
        this._allCustomers = a.Customers || []
        this._allProducts = a.Products || []
        this._allProductGroups = a.ProductGroups || []
        this._allSubModels = a.SubModels || []
    }

    get OnCustomersChanged(): BehaviorSubject<SimulationBO> {
        return this._onCustomersChanged
    }

    get OnProductsChanged(): BehaviorSubject<SimulationBO> {
        return this._onProductsChanged
    }

    get AllCustomers(): WB_CustomerRow[] {
        return this._allCustomers
    }

    get AllProducts(): WB_FinishedProductRow[] {
        return this._allProducts
    }

    get AllProductGroups(): WB_ProductGroupRow[] {
        return this._allProductGroups
    }

    //</editor-fold>

    //<editor-fold desc = "IHaveSimulationFunctions operations">
    private _OnSimulationFunctionsChanged: BehaviorSubject<{ self: SimulationBO; type: string; change?: any }>
    private _OnSimulationFunctionScopesChanged: BehaviorSubject<{ self: SimulationBO; type: string; change?: any }>

    private IHaveSimulationFunctions_Init(): void {
        this._OnSimulationFunctionsChanged = new BehaviorSubject<{ self: SimulationBO; type: string; change?: any }>({
            self: undefined,
            type: ""
        })
        this._OnSimulationFunctionScopesChanged = new BehaviorSubject<{
            self: SimulationBO;
            type: string;
            change?: any;
        }>({
            self: undefined,
            type: ""
        })
    }

    get SimulationFunctions(): WB_SpendFunctionRow[] {
        if (!(this.WB_Simulation.cif.WB_MODEL_PROFILE_ID > 0)) return []

        const profile: WB_ModelProfileRow = _.first(
            _.filter(
                this.Model.WB_Model.WB_ModelProfileRows,
                (row) => row.isActive && row.cif.WB_MODEL_PROFILE_ID == this.WB_Simulation.cif.WB_MODEL_PROFILE_ID
            )
        )
        if (_.isEmpty(profile)) return []

        const functionRows: WB_SpendFunctionRow[] = _.map(
            _.filter(profile.WB_ModelProfileSpendFunctionRows, (row) => row.isActive && row.cif.INCLUDE_IND == "1"),
            (row) => {
                return row.WB_SpendFunctionRow
            }
        )
        return functionRows
    }

    get SimulationFunctionRels(): WB_SpendFunctionRelationshipRow[] {
        return _.filter(this.WB_Simulation.WB_SpendFunctionRelationshipRows, (row) => row.isActive)
    }

    async addSimulationFunction(
        wbSpendFunctionId: number,
        opts: { [id: string]: any }
    ): Promise<WB_SpendFunctionRelationshipRow> {
        const self = this

        const functionRow = <WB_SpendFunctionRow>await self.dbContext.find(esWB_Spend_Function, wbSpendFunctionId)
        const newRow = <WB_SpendFunctionRelationshipRow>(
            await self.dbContext.createRecordWithKey(esWB_Spend_Function_Relationship)
        )
        newRow.setDefaults()
        newRow.cif.SPEND_ID = functionRow.cif.SPEND_FUNCTION_ID
        newRow.cif.SPEND_NAME = functionRow.cif.SPEND_FUNCTION_NAME
        newRow.cif.WB_SIMULATION_ID = self.WB_Simulation.cif.WB_SIMULATION_ID
        newRow.WB_SimulationRow = self.WB_Simulation
        newRow.cif.WB_SPEND_FUNCTION_ID = wbSpendFunctionId
        newRow.WB_SpendFunctionRow = functionRow

        if (functionRow.cif.FUNCTION_USAGE == 'Complex')
            newRow.cif.AMOUNT_LOCATION = 'Header'
        else
            newRow.cif.AMOUNT_LOCATION = functionRow.cif.AMOUNT_LOCATION
        newRow.cif.RATE_UOM = 'ConsumerUnit'
        newRow.cif.REDEMPTION_PCNT = 1

        self.dbContext.add(esWB_Simulation_Relationship, newRow)
        this._OnSimulationFunctionsChanged.next({self: self, type: opts.FunctionType, change: newRow})
        return newRow
    }

    async removeSimulationFunction(wbSpendFunctionRelationshipId: number, opts: { [id: string]: any }): Promise<void> {
        const self = this
        _.forEach(self.WB_Simulation.WB_SpendFunctionRelationshipRows, (row) => {
            if (row.isActive && row.cif.WB_SPEND_FUNCTIONR_ID == wbSpendFunctionRelationshipId) row.markDeleted()
        })
        this._OnSimulationFunctionsChanged.next({self: self, type: opts.FunctionType})
        return
    }

    async addSimulationFunctionSubModels(
        SpendFunctionRelRow: WB_SpendFunctionRelationshipRow,
        wbCustomerId: number,
        wbProdId?: number,
        wbPrdGrpId?: number,
        opts?: { [id: string]: any }
    ): Promise<WB_SpendFunctionRelationshipScopeRow> {
        const self = this
        const newRow = <WB_SpendFunctionRelationshipScopeRow>(
            await self.dbContext.createRecordWithKey(esWB_Spend_Function_Relationship_Scope)
        )
        newRow.setDefaults()
        newRow.cif.WB_SPEND_FUNCTIONR_ID = SpendFunctionRelRow.cif.WB_SPEND_FUNCTIONR_ID
        newRow.WB_SpendFunctionRelationshipRow = SpendFunctionRelRow

        if (wbCustomerId > 0) {
            let customerRow: WB_CustomerRow = undefined
            if (_.isEmpty(opts?.customer)) {
                customerRow = _.first(<WB_CustomerRow[]>await self.dbContext.findUsingCache(esWB_Customer, [wbCustomerId]))
            } else {
                customerRow = opts["customer"]
            }
            newRow.cif.WB_CUST_ID = wbCustomerId
            newRow.WB_CustomerRow = customerRow
        }

        if (wbPrdGrpId > 0) {
            let prdgrpRow: WB_ProductGroupRow = undefined
            if (self._bHasProductGroups) {
                if (_.isEmpty(opts) || !("prdgrp" in opts)) {
                    prdgrpRow = _.first(
                        <WB_ProductGroupRow[]>await self.dbContext.findUsingCache(esWB_Product_Group, [wbPrdGrpId])
                    )
                } else {
                    prdgrpRow = opts["prdgrp"]
                }
                newRow.cif.WB_PRDGRP_ID = wbPrdGrpId
                newRow.WB_ProductGroupRow = prdgrpRow
            }
        }

        let prodRow: WB_FinishedProductRow = undefined
        if (self._bHasProducts) {
            if (_.isEmpty(opts) || !("product" in opts)) {
                prodRow = _.first(
                    <WB_FinishedProductRow[]>await self.dbContext.findUsingCache(esWB_Finished_Product, [wbProdId])
                )
            } else {
                prodRow = opts["product"]
            }

            newRow.cif.WB_PROD_ID = wbProdId
            newRow.WB_ProductRow = prodRow
        }

        self.dbContext.add(esWB_Spend_Function_Relationship_Scope, newRow)
        self._OnSimulationFunctionScopesChanged.next({self: self, type: opts.FunctionType, change: newRow})
        return newRow
    }

    async removeSimulationFunctionSubModel(
        wbSpendFunctionSubModelId: number,
        opts: { [id: string]: any }
    ): Promise<void> {
        const self = this
        _.forEach(self.WB_Simulation.WB_SpendFunctionRelationshipRows, (row) => {
            if (!row.isActive) return
            _.forEach(row.WB_SpendFunctionRelationshipScopeRows, (sm) => {
                if (sm.isActive && sm.cif.WB_SPEND_FUNCTIONCPR_ID == wbSpendFunctionSubModelId) {
                    sm.markDeleted()
                    return
                }
            })
        })
        self._OnSimulationFunctionScopesChanged.next({self: self, type: opts.FunctionType})
        return
    }

    // Need to get list of SubModels for Scoping Function
    get SubModels(): ICoefficientSetSubModel[] {
        const self = this

        const simulationCustomer = self.WB_Simulation.WB_CustomerRow
        let simulationProducts: WB_ProductGroupRow[] | WB_FinishedProductRow[] = []
        if (this._bHasProducts)
            simulationProducts = _.map(self.WB_Simulation.WB_SimulationProductRows, (row) => row.WB_ProductRow)
        else simulationProducts = _.map(self.WB_Simulation.WB_SimulationProductRows, (row) => row.WB_ProductGroupRow)

        const arrSubModels: ICoefficientSetSubModel[] = []
        _.forEach(simulationProducts, (aProduct: WB_ProductGroupRow | WB_FinishedProductRow) => {
            const aSubModel = {
                Customer: simulationCustomer,
                ProductGroup: this._bHasProducts ? undefined : <WB_ProductGroupRow>aProduct,
                Product: this._bHasProducts ? <WB_FinishedProductRow>aProduct : undefined
            }
            arrSubModels.push(aSubModel)
        })

        this._subModels = arrSubModels || []
        return this._subModels
    }

    get OnSimulationFunctionsChanged(): BehaviorSubject<{ self: SimulationBO; type: string; change?: any }> {
        return this._OnSimulationFunctionsChanged
    }

    get OnSimulationFunctionScopesChanged(): BehaviorSubject<{ self: SimulationBO; type: string; change?: any }> {
        return this._OnSimulationFunctionScopesChanged
    }

    //</editor-fold>

    //<editor-fold desc = "Record Resolver Operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ("dbContext" in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }

        const simulation_options: CreateOptions = {
            standardConditions: {
                [esWB_Simulation]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Simulation_Product]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Simulation_Predictor_Relationship]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Simulation_Input_Relationship]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Spend_Function_Relationship]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Spend_Function_Relationship_Scope]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Simulation_Metrics]: {DELETED_IND: {notIn: ["1"]}, SUMMARY_LEVEL: "CustomerProductPeriod"},
                [esWB_Published_Model]: {DELETED_IND: {notIn: ["1"]}},
                [esWB_Simulation_Input]: {DELETED_IND: {notIn: ["1"]}}
            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Published_Model]: [esWB_Model, esWB_Model_Coeff_Set, esWB_User],
                [esWB_Model_Profile]: [esWB_Model_Profile_Group],
                [esWB_Model_Profile_Group]: [esWB_Model_Profile_Group_Predictor],
                [esWB_Spend_Function]: [esWB_Spend_Function_Input_Relationship]
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_Simulation_Metrics]: [esWB_Simulation_Metrics_CBO_Set]
            }
        }

        return new ESRecordResolver(dbContext, simulation_options)
    }

    static async setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        await recordResolver.findAllWithRelationships(esWB_Simulation, ids)
    }

    static concurrentLoadCountHint(): number {
        return 10
    }

    //</editor-fold>
}

PAEnvironment.registerBusinessObjectClass(SimulationBO, "SimulationBO", "Simulation")
